import { array, fn } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import { or } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import ApplicationHistory from 'tio-common/components/tas/application-history';
import ApproverComments from 'tio-common/components/tuition-assistance/approver-comments';
import Component from '@glimmer/component';
import ENV from 'tio-employee/config/environment';
import TasAppDetailsEmployeeEligibility from 'tio-common/components/tuition-assistance/application-details/employee-eligibility';
import AlertBanner from 'tio-ui/components/alert-banner';
import TioAppearance from 'tio-common/components/tio/appearance';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import TuitionAssistanceApplicationDetailsApprovalQueueStatus from 'tio-common/components/tuition-assistance/application-details/approval-queue-status';
import TuitionAssistanceApplicationDetailsApproverOverview from 'tio-common/components/tuition-assistance/application-details/approver-overview';
import TuitionAssistanceApplicationDetailsCoursesStatusApproval from 'tio-common/components/tuition-assistance/application-details/courses-status-approval';
import TuitionAssistanceApplicationDetailsEmployeeInformation from 'tio-common/components/tuition-assistance/application-details/employee-information';
import TuitionAssistanceProgramDetailsAppDetailsApproverCourseCard from 'tio-common/components/tuition-assistance/program-details/app-details/approver-course-card';
import TuitionAssistanceProgramDetailsAppDetailsReadonlyCourseCard from 'tio-common/components/tuition-assistance/program-details/app-details/readonly-course-card';
import TuitionAssistanceProgramDetailsAppDetailsScholarshipCard from 'tio-common/components/tuition-assistance/program-details/app-details/scholarship-card';
import TuitionAssistanceProgramDetailsApplicationInformation from 'tio-common/components/tuition-assistance/program-details/application-information';
import TuitionAssistanceProgramDetailsProgramInfoCard from 'tio-common/components/tuition-assistance/program-details/program-info-card';
import AttachmentList from 'tio-common/components/tuition-assistance/forms/upload-attachments/attachment-list';
import PaymentDetailsDrawer from 'tio-employee/components/drawers/payment-details-drawer';
import type { ReceivedScholarship } from 'tio-common/types/tuition-assistance';
import type Owner from '@ember/owner';
import type SessionContextService from 'tio-employee/services/session-context';
import type SessionService from 'tio-employee/services/session';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type FeaturesService from 'tio-common/services/features';
import { PhCloudArrowUp } from 'ember-phosphor-icons';
import Modal from 'tio-ui/components/modal';
import ReSelect from 'tio-ui/components/select';
import TuitionAssistanceFormsUploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';

interface RouteSignature {
  Args: {
    model: TasApplicationModel;
  };
}

const classificationOptions = [
  { label: 'Final Approval Document', key: 'COMPLETION_DOCUMENTS' },
  { label: 'Approval Document', key: 'COURSES_APPROVAL_DOCUMENTS' },
  { label: 'Misc Approval Document (Admin)', key: 'APPLICATION_LIFECYCLE_DOCUMENTS' },
];

export default class TuitionAssistanceApplicationsShowIndexRouteComponent extends Component<RouteSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare session: SessionService;
  @service declare store: StoreService;
  @service declare features: FeaturesService;
  @tracked dynamicEligibility?: boolean;
  @tracked programEligibility: [string, string][] = [];
  @tracked uploadModalOpen = false;
  @tracked selectedClassification = '';
  @tracked uploadClassificationField = '';

  applicationFieldsCopy!: TasApplicationModel['fields'];

  constructor(owner: Owner, args: RouteSignature['Args']) {
    super(owner, args);
    this.applicationFieldsCopy = trackedObject(this.args.model.fields);
    this.loadEligibility();

    const courseApprovalTypes = ['TAS.ProgramType.3', 'TAS.ProgramType.4.3'];
    const coursePreApprovalStates = [
      'TAS.ApplicationState.DEFINE_COURSES',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    ];

    const { typeClassification, state } = this.args.model;
    const defaultAttachmentClassification =
      courseApprovalTypes.includes(typeClassification) && coursePreApprovalStates.includes(state)
        ? 'COURSE_APPROVAL_DOCUMENTS'
        : 'COMPLETION_DOCUMENTS';
    this.uploadClassificationField = defaultAttachmentClassification;
    this.selectedClassification = defaultAttachmentClassification;
  }

  async loadEligibility() {
    const { id } = this.args.model.tasProgramInstance.employee.tasParticipant;
    const { code } = this.args.model.tasProgramInstance.tasProgramTemplate;

    const dynamicEligibility = this.store
      .adapterFor('tas-participant')
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      .dynamicEligibility(id, code);

    const programEligibility = this.store
      .adapterFor('tas-participant')
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      .periodOfEligibility(id, code);

    this.dynamicEligibility = await dynamicEligibility;
    this.programEligibility = await programEligibility;
  }

  get lockEmployeeStatusBasedOn() {
    return this.args.model.tasProgramInstance.tasProgramTemplate.lockEmployeeStatusBasedOn;
  }

  calculateEligibility() {
    if (!this.programEligibility.length) {
      return false;
    }
    const startDate = getSingleValueForTasField(
      'COURSES_BEGIN_DATE',
      this.args.model.fields
    ) as string;

    const endDate = getSingleValueForTasField('COURSES_END_DATE', this.args.model.fields) as string;
    const datesToCheck = {
      COURSE_START_DATE: new Date(startDate),
      COURSE_END_DATE: new Date(endDate),
      FULFILLED_DATE: new Date(),
    };
    const dateBasedOnLock = this.lockEmployeeStatusBasedOn
      ? datesToCheck[this.lockEmployeeStatusBasedOn as keyof typeof datesToCheck]
      : new Date();

    return this.programEligibility.some(([start, end]) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      return dateBasedOnLock >= startDate && dateBasedOnLock <= endDate;
    });
  }

  get applicationIsIneligible() {
    const eligibleLocks = ['ON_SUBMISSION', 'ON_COURSES_SUBMISSION'];
    if (eligibleLocks.includes(this.lockEmployeeStatusBasedOn as string)) {
      return false;
    }
    if (
      (this.lockEmployeeStatusBasedOn === 'FULFILLED_DATE' || !this.lockEmployeeStatusBasedOn) &&
      this.args.model.state !== 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
    ) {
      return false;
    }
    return !(this.dynamicEligibility && this.calculateEligibility());
  }

  get canEditTasApplication() {
    return this.sessionContext.canEditTasApplication && this.args.model.approverCanEditApplication;
  }

  get approverName() {
    return this.sessionContext.user?.person?.fullName || '';
  }

  get allAttachments() {
    // Assets can be attached to the course or the application
    const courseAssets = this.args.model.activeCourses.flatMap((course) => course.tasAssets || []);
    const appAssets = this.args.model.tasAssets;
    return [...courseAssets, ...appAssets];
  }

  get partnerMessagingEnabled() {
    const companySetting =
      this.args.model.tasProgramInstance.tasProgramTemplate.company.companySetting;
    return companySetting?.tuitionAssistance?.isPartnerMessagingEnabled;
  }

  didUpdateScholarships = (editableScholarships: ReceivedScholarship[], changelog?: string) => {
    this.applicationFieldsCopy['SCHOLARSHIPS_RECEIVED'] = {
      values: editableScholarships,
    };
    if (changelog) {
      this.saveScholarships.perform(changelog);
    }
  };

  saveScholarships = dropTask(async (changelog: string) => {
    this.args.model.fields = this.applicationFieldsCopy;
    try {
      await this.args.model.save({ adapterOptions: { changelog: changelog } });
    } catch (e) {
      console.error(e);
    }
  });

  setUploadClassification = (classification: string) => {
    this.selectedClassification = classification;
    this.uploadClassificationField = classification || 'APPLICATION_LIFECYCLE_DOCUMENTS';
  };

  <template>
    <div class="tio-row-between sticky top-0 tio-bg-sunken">
      <TioPageBreadcrumbs as |b|>
        <b.crumb @route="authenticated.admin.tas.approval-queue" @label="Applications" />
        <b.crumb
          @route="authenticated.admin.tuition-assistance.applications.show.index"
          @model={{@model.id}}
          @label={{@model.displayName}}
        />

      </TioPageBreadcrumbs>
      <TioAppearance
        @apiKey={{ENV.apiKey}}
        @apiHost={{ENV.apiHost}}
        @token={{this.session.data.authenticated.access_token}}
        @type="tas_course_application"
        @documentId={{or @model.id ""}}
        class="inline-block"
      />
    </div>
    {{#if @model.tasProgramInstance.employee.tasParticipant.isHeld}}
      <AlertBanner
        @intent="warning"
        @title={{t "tuition_assistance.alert.employee_held_pt_1"}}
        class="max-w-8xl"
      >
        <p class="text-sm">{{t "tuition_assistance.alert.employee_held_pt_2"}}</p>
      </AlertBanner>
    {{/if}}
    <HStack>
      <Section>
        <VStack @collapsed={{true}}>
          <TuitionAssistanceApplicationDetailsEmployeeInformation
            @instance={{@model.tasProgramInstance}}
            @profileRoute="authenticated.admin.employees.show"
            @routeModel={{@model.tasProgramInstance.employee.id}}
          />
          <TuitionAssistanceProgramDetailsApplicationInformation
            @application={{@model}}
            @isAdminView={{true}}
            @canRequestRepayment={{this.sessionContext.user.isAccountOwnerOrEmployeeAdmin}}
            @canEditTasApplication={{this.canEditTasApplication}}
            @isReadonlyView={{false}}
            @requestingApp="employee"
          />
          <TuitionAssistanceProgramDetailsProgramInfoCard
            @programInstance={{@model.tasProgramInstance}}
            @showAttachments={{true}}
            @canEditTasApplication={{this.canEditTasApplication}}
          />
        </VStack>
      </Section>
      <Section>
        <VStack @collapsed={{true}}>
          <ApproverComments
            @commentable={{@model}}
            @commentableType="TasApplication"
            @authoredByPerson={{this.sessionContext.user.person}}
            @commentTags={{array "approver"}}
          />
          {{#if this.partnerMessagingEnabled}}
            <ApproverComments
              @commentable={{@model}}
              @commentableType="TasApplication"
              @authoredByPerson={{this.sessionContext.user.person}}
              @commentTags={{array "Partner Messaging"}}
            />
          {{/if}}
          <TuitionAssistanceApplicationDetailsApproverOverview
            @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          />
        </VStack>
      </Section>
    </HStack>
    <Section>
      <:body>
        <div class="tio-flex-center flex-wrap mb-4 gap-5 md:justify-start">
          {{#each @model.activeCourses as |course|}}
            {{#if this.canEditTasApplication}}
              <div>
                <TuitionAssistanceProgramDetailsAppDetailsApproverCourseCard
                  @course={{course}}
                  @showCustomFields={{true}}
                  @approverName={{this.approverName}}
                  @application={{@model}}
                />
              </div>
            {{else}}
              <div>
                <TuitionAssistanceProgramDetailsAppDetailsReadonlyCourseCard
                  @course={{course}}
                  @showCustomFields={{true}}
                />
              </div>
            {{/if}}
          {{/each}}
          <div>
            <TuitionAssistanceProgramDetailsAppDetailsScholarshipCard
              @application={{@model}}
              @isEditable={{this.canEditTasApplication}}
              @isAdminView={{true}}
              @onUpdateScholarships={{this.didUpdateScholarships}}
            />
          </div>
        </div>
      </:body>
    </Section>
    <Section>
      <:header>
        <h2 class="tio-text-lg-semibold">
          {{t "tuition_assistance.program_details.attachments"}}
        </h2>
      </:header>
      <:actions>
        <button type="button" {{on "click" (fn (mut this.uploadModalOpen) true)}}>
          <PhCloudArrowUp @size="28" class="mb-2 tio-fg-info" />
        </button>
      </:actions>
      <:body>
        {{#if this.allAttachments.length}}
          <AttachmentList
            @assets={{this.allAttachments}}
            @disabled={{false}}
            @hideDiscarded={{true}}
            class="w-full"
          />
        {{else}}
          <p class="text-sm tio-fg-muted">
            {{t "tuition_assistance.program_details.no_attachments"}}
          </p>
        {{/if}}
      </:body>
    </Section>
    <TuitionAssistanceApplicationDetailsApprovalQueueStatus
      @application={{@model}}
      @consumingApp="employee"
      @isReadonlyView={{false}}
    />
    <TasAppDetailsEmployeeEligibility @programInstance={{@model.tasProgramInstance}} />
    <HStack>
      <Section>
        {{! TODO: This is a good example of over componentization. The lmits summary component inside
          this component makes it nearly impossible to have the functionality laid out properly in
          various UI contexts. Specifically tio-admin and partner-admin use this, but need different
          sectioning.
        }}
        {{#if this.applicationIsIneligible}}
          <div class="p-8">
            <AlertBanner
              @intent="warning"
              @title={{t "tuition_assistance.alert.application_ineligible"}}
            >
              <p class="text-sm">
                {{t "tuition_assistance.alert.application_ineligible_reason"}}
              </p>
            </AlertBanner>
          </div>
        {{/if}}
        <TuitionAssistanceApplicationDetailsCoursesStatusApproval
          @application={{@model}}
          @programInstance={{@model.tasProgramInstance}}
          @routePrefix="authenticated.admin"
          @isReadonlyView={{false}}
        />
        <PaymentDetailsDrawer @programInstance={{@model.tasProgramInstance}} />
      </Section>
    </HStack>
    <ApplicationHistory
      @history={{@model.fullHistory}}
      @title={{t "common.tuition_assistance.applications.history.header"}}
      @application={{@model}}
    />
    <Modal @isOpen={{this.uploadModalOpen}} @onClose={{fn (mut this.uploadModalOpen) false}}>
      <:header>{{t "tuition_assistance.program_details.upload_attachments"}}</:header>
      <:body>
        <label for="classification-type" class="tio-text-sm-semibold tio-fg-secondary block mb-1">
          {{t "tuition_assistance.program_details.courses.document_type"}}
        </label>
        <ReSelect
          data-test-classification-type
          @items={{classificationOptions}}
          @selected={{this.selectedClassification}}
          @onChange={{this.setUploadClassification}}
          @id="classification-type"
          @clearable={{false}}
        />
        <TuitionAssistanceFormsUploadAttachments
          @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          @classification={{this.uploadClassificationField}}
          @attachable={{@model}}
          @attachableType="tasApplication"
          @disabled={{false}}
          @hideAttachments={{true}}
          @didUploadFile={{fn (mut this.uploadModalOpen) false}}
        />
      </:body>
    </Modal>
  </template>
}
